import { Component, Suspense, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Alert, Button, Spinner } from '@/components/ui';
import Sidebar from './Sidebar';
import './Layout.css';

function RouteFallback() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '48px 0',
      }}
    >
      <Spinner size="lg" label="Loading page…" />
    </div>
  );
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  failed: boolean;
}

// Keeps the sidebar up when a page throws or its lazy chunk is gone after a deploy replaced the hashed assets.
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { failed: true };
  }

  render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children;
    }
    return (
      <div style={{ padding: '48px 24px', maxWidth: 560 }}>
        <Alert variant="error" title="This page failed to load">
          A deploy may have replaced the code this tab was opened with. Reload
          to fetch the current version.
          <div style={{ marginTop: 12 }}>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        </Alert>
      </div>
    );
  }
}

// Both boundaries sit inside Layout so the sidebar stays mounted; the pathname key clears a failure on navigation.
function Layout() {
  const { pathname } = useLocation();
  return (
    <div className="layout">
      <Sidebar />
      <main className="main">
        <RouteErrorBoundary key={pathname}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </main>
    </div>
  );
}

export default Layout;
